import { Box, Skeleton, Stack } from "@mui/material";
import { Card } from "@platform/design-system";

/**
 * Named for a screen reader, which sees no shape — only that something is
 * loading and, without this, not what.
 */
const LOADING_LABEL = "Loading census validation";

/** How many check rows a placeholder line shows. Most lines ask for five. */
const CHECKS_PER_LINE = 5;

/** How many lines to stand in for. Most RFPs quote three or four. */
const PLACEHOLDER_LINES = 3;

const CheckRow = (): React.JSX.Element => (
  <Stack direction="row" alignItems="center" gap={1} sx={{ py: 1 }}>
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton variant="text" width={160} />
  </Stack>
);

const LinePlaceholder = (): React.JSX.Element => (
  <Card sx={{ p: 2 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 1 }}
    >
      <Skeleton variant="text" width={120} />
      <Skeleton variant="text" width={70} />
    </Stack>

    <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
      {Array.from({ length: CHECKS_PER_LINE }, (_, index) => (
        <CheckRow key={index} />
      ))}
    </Stack>
  </Card>
);

const SidebarPlaceholder = (): React.JSX.Element => (
  <Card sx={{ p: 2 }}>
    <Stack gap={1}>
      <Skeleton variant="text" width={140} />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="75%" />
    </Stack>
  </Card>
);

/**
 * The page's shape while it loads.
 *
 * Laid out like the real thing so the content does not jump when it arrives,
 * and so the wait reads as a page filling in rather than a blank panel.
 */
export const CensusValidationSkeleton = (): React.JSX.Element => (
  <Stack
    gap={3}
    data-testid="census-validation-skeleton"
    role="progressbar"
    aria-busy="true"
    aria-label={LOADING_LABEL}
  >
    <Stack gap={0.5}>
      <Skeleton variant="text" width={280} height={44} />
      <Skeleton variant="text" width={420} />
    </Stack>

    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
        alignItems: "start",
      }}
    >
      <Card sx={{ p: 2 }}>
        <Stack gap={2}>
          {Array.from({ length: PLACEHOLDER_LINES }, (_, index) => (
            <LinePlaceholder key={index} />
          ))}
        </Stack>
      </Card>

      <Stack gap={2}>
        <SidebarPlaceholder />
        <SidebarPlaceholder />
      </Stack>
    </Box>
  </Stack>
);
